import React, { useContext } from "react";
import { ChevronDownIcon } from "lucide-react";
import { useCart } from "../../contexts/CartContext";
import { ThemeContext } from "../../contexts/ThemeContext"; // Import ThemeContext
import { useNavigate } from "react-router-dom";

export default function CheckoutPage() {
  const { cartProducts, currency } = useCart();
  const { isDarkMode } = useContext(ThemeContext); // Get dark mode state

  const navigate = useNavigate();

  return (
    <div
      className={`${
        isDarkMode ? "bg-black text-white" : "bg-gray-200 text-black"
      } font-sans min-h-screen p-6`}
    >
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Customer Details Form */}
        <div
          className={`flex-grow ${
            isDarkMode ? "bg-gray-800" : "bg-gray-200"
          } rounded-lg p-6`}
        >
          <h2 className="text-2xl font-bold mb-6">Customer Details</h2>
          <form className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium mb-1"
                >
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  placeholder="First Name *"
                  className={`w-full ${
                    isDarkMode ? "bg-gray-700" : "bg-white"
                  } rounded-md p-2 text-white placeholder-gray-400`}
                />
              </div>
              <div>
                <label
                  htmlFor="middleName"
                  className="block text-sm font-medium mb-1"
                >
                  Middle Name *
                </label>
                <input
                  type="text"
                  id="middleName"
                  placeholder="Middle Name *"
                  className={`w-full ${
                    isDarkMode ? "bg-gray-700" : "bg-white"
                  } rounded-md p-2 text-white placeholder-gray-400`}
                />
              </div>
              <div>
                <label
                  htmlFor="thirdName"
                  className="block text-sm font-medium mb-1"
                >
                  Third Name *
                </label>
                <input
                  type="text"
                  id="thirdName"
                  placeholder="Third Name *"
                  className={`w-full ${
                    isDarkMode ? "bg-gray-700" : "bg-white"
                  } rounded-md p-2 text-white placeholder-gray-400`}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="country"
                  className="block text-sm font-medium mb-1"
                >
                  Country *
                </label>
                <div className="relative">
                  <select
                    id="country"
                    className={`w-full ${
                      isDarkMode ? "bg-gray-700" : "bg-white"
                    } rounded-md p-2 text-white appearance-none`}
                  >
                    <option value="">Country</option>
                  </select>
                  <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
                </div>
              </div>
              <div>
                <label
                  htmlFor="civilNo"
                  className="block text-sm font-medium mb-1"
                >
                  Civil No *
                </label>
                <input
                  type="text"
                  id="civilNo"
                  placeholder="Civil No *"
                  className={`w-full ${
                    isDarkMode ? "bg-gray-700" : "bg-white"
                  } rounded-md p-2 text-white placeholder-gray-400`}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium mb-1"
                >
                  Phone *
                </label>
                <input
                  type="tel"
                  id="phone"
                  placeholder="Phone"
                  className={`w-full ${
                    isDarkMode ? "bg-gray-700" : "bg-white"
                  } rounded-md p-2 text-white placeholder-gray-400`}
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mb-1"
                >
                  E-mail *
                </label>
                <input
                  type="email"
                  id="email"
                  placeholder="E-mail *"
                  className={`w-full ${
                    isDarkMode ? "bg-gray-700" : "bg-white"
                  } rounded-md p-2 text-white placeholder-gray-400`}
                />
              </div>
            </div>
            <p className="text-[#ffd700]">Have an account?</p>
            <div>
              <h3 className="font-bold mb-2">Select Method of Receiving</h3>
              <label className="flex items-center space-x-2 cursor-pointer">
                <div className="w-5 h-5 rounded-full border-2 border-[#ffd700] flex items-center justify-center">
                  <div className="w-3 h-3 rounded-full bg-[#ffd700]"></div>
                </div>
                <span>Store pickup</span>
              </label>
            </div>
          </form>
        </div>

        {/* Order Summary */}
        <div className="w-full lg:w-80 space-y-6">
          <div
            className={`rounded-lg p-4 ${
              isDarkMode ? "bg-gray-800" : "bg-gray-200"
            }`}
          >
            <h2 className="text-xl font-bold mb-4">
              Products ({cartProducts.length})
            </h2>
            {cartProducts.map((product) => (
              <div
                key={product.cart_product_id}
                className="flex items-center space-x-4"
              >
                <img
                  src={product.photo[0].url}
                  alt={product.name}
                  className="w-8 h-8 object-cover rounded"
                />
                <div>
                  <p className="font-semibold">{product.name}</p>
                  <p className="text-sm text-gray-400">{product.qty} x</p>
                </div>
                <span dangerouslySetInnerHTML={{ __html: currency }} />
                <p className="ml-auto font-bold">{product.price} </p>
              </div>
            ))}
          </div>

          <div
            className={`rounded-lg p-4 ${
              isDarkMode ? "bg-gray-800" : "bg-gray-200"
            }`}
          >
            <h2 className="text-xl font-bold mb-4">Order Summary</h2>
            <div className="flex justify-between mb-2">
              <span>Total price of products</span>
              <span className="font-bold">
                <span dangerouslySetInnerHTML={{ __html: currency }} />
                {cartProducts
                  .reduce(
                    (acc, product) => acc + product.price * product.qty,
                    0
                  )
                  .toFixed(2)}{" "}
              </span>
            </div>
            <div className="border-t border-gray-600 my-2"></div>
            <div className="flex justify-between text-xl font-bold">
              <span>Total</span>
              <span>
                <span dangerouslySetInnerHTML={{ __html: currency }} />
                {cartProducts
                  .reduce(
                    (acc, product) => acc + product.price * product.qty,
                    0
                  )
                  .toFixed(2)}{" "}
              </span>
            </div>
            <button
              onClick={() => navigate("/order")}
              className="w-full bg-[#3a3a3a] text-white py-3 rounded-lg font-bold text-lg mt-4 hover:bg-[#4a4a4a] transition duration-300"
            >
              Checkout
            </button>
          </div>
        </div>
      </div>

      {/* Help Button */}
      {/* <button className="fixed bottom-4 left-4 bg-[#ffd700] text-black px-4 py-2 rounded-full font-bold flex items-center space-x-2">
        <span className="text-2xl">?</span>
        <span>Help</span>
      </button> */}
    </div>
  );
}
